import React, { useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useLanguage } from '../../context/LanguageContext';

export default function RevolutionaryHealthcare() {
  const { t, isRTL } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(1);

  const features = [
    {
      id: 1,
      icon: "🤖",
      title:       t('comingSoon.features.aiReports.title')       || 'AI-Powered Reports',
      description: t('comingSoon.features.aiReports.description') || 'Automated medical report generation with high accuracy and speed',
      borderColor: 'border-teal-500',
    },
    {
      id: 2,
      icon: "📱",
      title:       t('comingSoon.features.smartFollowups.title')       || 'Smart Follow-ups',
      description: t('comingSoon.features.smartFollowups.description') || 'Intelligent patient tracking and automated follow-up reminders',
      borderColor: 'border-secondary',
      highlighted: true
    },
    {
      id: 3,
      icon: "👨‍⚕️",
      title:       t('comingSoon.features.virtualAssistant.title')       || 'Virtual Assistant',
      description: t('comingSoon.features.virtualAssistant.description') || 'Supporting doctors with smart tools and seamless technology',
      borderColor: 'border-teal-500',
    }
  ];

  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % features.length);

  const PrevIcon = isRTL ? <RightOutlined className="text-xl" /> : <LeftOutlined  className="text-xl" />;
  const NextIcon = isRTL ? <LeftOutlined  className="text-xl" /> : <RightOutlined className="text-xl" />;

  return (
    <section
      dir={isRTL ? 'rtl' : 'ltr'}
      className="py-12 sm:py-16 md:pb-32 bg-cardColor min-h-screen"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary_hover mb-4 sm:mb-6 px-4">
            {t('comingSoon.subtitle') || 'Revolutionary Healthcare Experience'}
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-primary max-w-4xl mx-auto leading-relaxed px-4">
            {t('comingSoon.description') || 'Transforming healthcare with AI-powered medical reporting, intelligent follow-ups, and virtual assistance. Reducing manual workload for doctors while improving accuracy, speed, and patient engagement.'}
          </p>
        </div>

        {/* Desktop — 3 cards */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 lg:gap-16 mt-12 lg:mt-24">
          {features.map((feature) => (
            <div
              key={feature.id}
              className={`rounded-2xl border-2 ${feature.borderColor} p-6 lg:p-8 transition-all duration-300 hover:shadow-xl ${
                feature.highlighted ? 'transform scale-110 lg:scale-125 shadow-lg' : 'hover:scale-105'
              } flex flex-col items-center text-center min-h-[280px] lg:min-h-[300px]`}
            >
              <div className="mb-3 lg:mb-4 text-4xl lg:text-5xl">{feature.icon}</div>
              <h3 className="text-lg lg:text-xl xl:text-2xl font-bold text-teal-800 mb-2 lg:mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm lg:text-base leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden mt-8">
          <div className="relative flex flex-col items-center">

            <div className="w-full max-w-sm px-4">
              <div className={`rounded-2xl border-2 ${features[currentIndex].borderColor} p-6 shadow-lg flex flex-col items-center text-center min-h-[280px]`}>
                <div className="mb-4 text-5xl">{features[currentIndex].icon}</div>
                <h3 className="text-xl font-bold text-teal-800 mb-3">
                  {features[currentIndex].title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {features[currentIndex].description}
                </p>
              </div>
            </div>

            {/* Mobile navigation */}
            <div className="flex items-center justify-center gap-6 mt-6">
              <button
                onClick={handlePrev}
                className="w-12 h-12 rounded-full border-2 border-teal-500 text-teal-500 hover:bg-teal-500 hover:text-white transition-all flex items-center justify-center shadow-md active:scale-95"
                aria-label={t('comingSoon.features.prevLabel') || 'Previous feature'}
              >
                {PrevIcon}
              </button>

              <div className="flex gap-2">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-3 rounded-full transition-all ${
                      index === currentIndex ? 'bg-teal-500 w-8' : 'w-3 bg-gray-300'
                    }`}
                    aria-label={`${t('comingSoon.features.goToLabel') || 'Go to feature'} ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                className="w-12 h-12 rounded-full border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white transition-all flex items-center justify-center shadow-md active:scale-95"
                aria-label={t('comingSoon.features.nextLabel') || 'Next feature'}
              >
                {NextIcon}
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}